/**
 * Operational status helpers for Quote Ops OS (Sprint 8.9 Phase 1).
 * Pure functions — buckets / next action / age / timeline derived from existing Quote fields.
 * CRM `status` enum is unchanged; display buckets tell the customer-offer story.
 */

import {
  areQuotePdfsReady,
  needsAutomatedPreparationApproval,
} from "../utils/quotePdfReadiness.js";
import {
  formatServiceTypeLabel,
  isSharedLoadType,
  SERVICE_TYPE,
} from "../../../shared/utils/serviceTypeLanguage.js";

export const QUOTE_OPS_BUCKETS = Object.freeze([
  "draft",
  "ready_to_send",
  "sent",
  "viewed",
  "negotiating",
  "deposit_requested",
  "deposit_received",
  "accepted",
  "expired",
  "superseded",
  "rejected",
  "ordered",
]);

const BUCKET_LABELS = Object.freeze({
  draft: "Draft",
  ready_to_send: "Ready to Send",
  sent: "Sent",
  viewed: "Viewed",
  negotiating: "Negotiating",
  deposit_requested: "Deposit Requested",
  deposit_received: "Deposit Received",
  accepted: "Accepted",
  expired: "Expired",
  superseded: "Superseded",
  rejected: "Rejected",
  ordered: "Ordered",
  unknown: "Unknown",
});

/**
 * @param {string} bucket
 * @returns {string}
 */
export function getQuoteOperationalLabel(bucket) {
  return BUCKET_LABELS[bucket] || BUCKET_LABELS.unknown;
}

/**
 * @param {Object} quote
 * @returns {number}
 */
export function getQuoteTotal(quote) {
  if (quote?.totalCost != null && Number.isFinite(Number(quote.totalCost))) {
    return Number(quote.totalCost);
  }
  const estimated = Number(quote?.estimatedCost) || 0;
  const addOns = Number(quote?.addOnsCost) || 0;
  return estimated + addOns;
}

/**
 * @param {Object} quote
 * @returns {number}
 */
export function getQuoteDeposit(quote) {
  const fromPayment = Number(quote?.payment?.depositAmount);
  if (Number.isFinite(fromPayment) && fromPayment > 0) return fromPayment;
  if (quote?.payment?.requiredAmountOverridden === true) {
    const overriddenAmount = Number(quote?.payment?.requiredAmount);
    if (Number.isFinite(overriddenAmount) && overriddenAmount >= 0) {
      return overriddenAmount;
    }
  }
  const fromApi = Number(quote?.deposit?.requiredAmount);
  if (Number.isFinite(fromApi) && fromApi >= 0) return fromApi;
  const fromSop = Number(quote?.sop2Messaging?.depositAmount);
  if (Number.isFinite(fromSop) && fromSop > 0) return fromSop;
  return 0;
}

/**
 * @param {number|null|undefined} amount
 * @returns {string}
 */
export function formatQuoteMoney(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  const n = Math.round(Number(amount));
  return `R${n.toLocaleString("en-ZA")}`;
}

/**
 * Customer-facing quote lifecycle bucket (ADR-002).
 * Precedence: terminal → money → negotiation → send → draft readiness.
 * @param {Object} quote
 * @returns {keyof typeof BUCKET_LABELS}
 */
export function getQuoteOperationalBucket(quote) {
  if (!quote) return "unknown";
  const status = quote.status;

  if (status === "rejected") return "rejected";
  if (status === "superseded") return "superseded";
  if (status === "expired") return "expired";
  if (status === "ordered") return "ordered";

  if (status === "deposit_paid" || status === "deposit_verified") {
    return "deposit_received";
  }

  if (status === "pending_payment") return "deposit_requested";

  if (status === "accepted" || status === "approved") {
    const deposit = getQuoteDeposit(quote);
    const paid = Boolean(
      quote?.payment?.depositPaid || quote?.payment?.depositVerified
    );
    if (deposit > 0 && !paid) return "deposit_requested";
    return "accepted";
  }

  if (status === "negotiating") return "negotiating";

  if (status === "sent") {
    if (quote?.viewedAt || quote?.customerViewedAt) return "viewed";
    return "sent";
  }

  if (status === "send_queued") return "ready_to_send";

  if (status === "pending") {
    // PDFs regenerate on send — pending quotes are sendable once approved
    return "ready_to_send";
  }

  return "draft";
}

/**
 * @param {Object} quote
 * @returns {{ key: string, label: string }}
 */
export function getQuoteNextAction(quote) {
  const bucket = getQuoteOperationalBucket(quote);

  switch (bucket) {
    case "draft":
      return { key: "send_quote", label: "Send Email" };
    case "ready_to_send":
      if (needsAutomatedPreparationApproval(quote)) {
        return { key: "review_pricing", label: "Review & Approve" };
      }
      return { key: "send_quote", label: "Send Email" };
    case "sent":
    case "viewed":
      return { key: "follow_up", label: "Follow Up" };
    case "negotiating":
      return { key: "respond", label: "Respond" };
    case "deposit_requested":
      return { key: "check_payment", label: "Check Payment" };
    case "deposit_received":
      return { key: "confirm_order", label: "Confirm Order" };
    case "accepted":
      return getQuoteDeposit(quote) === 0
        ? { key: "confirm_order", label: "Confirm Deposit Waiver" }
        : { key: "request_deposit", label: "Request Deposit" };
    case "superseded":
      return { key: "view_revision", label: "Open Revision" };
    case "expired":
      return { key: "renew", label: "Renew Quote" };
    case "rejected":
      return { key: "none", label: "—" };
    case "ordered":
      return { key: "view_order", label: "View Order" };
    default:
      return { key: "review", label: "Review" };
  }
}

/**
 * @param {Object} quote
 * @param {Date} [now]
 * @returns {number|null} hours since last meaningful update
 */
export function getQuoteAgeHours(quote, now = new Date()) {
  const raw =
    quote?.updatedAt || quote?.sentAt || quote?.createdAt || null;
  if (!raw) return null;
  const then = new Date(raw);
  if (Number.isNaN(then.getTime())) return null;
  return (now.getTime() - then.getTime()) / (1000 * 60 * 60);
}

/**
 * Compact relative age for list scan (e.g. "2h ago", "3d ago").
 * @param {Object} quote
 * @param {Date} [now]
 * @returns {string}
 */
export function formatQuoteAge(quote, now = new Date()) {
  const hours = getQuoteAgeHours(quote, now);
  if (hours == null) return "—";
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins}m ago`;
  }
  if (hours < 48) return `${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/**
 * @param {Object[]} quotes
 * @returns {Record<string, number>}
 */
export function computeQuoteOperationalSummary(quotes = []) {
  const summary = Object.fromEntries(
    QUOTE_OPS_BUCKETS.map((bucket) => [bucket, 0])
  );
  for (const quote of quotes) {
    const bucket = getQuoteOperationalBucket(quote);
    if (summary[bucket] != null) summary[bucket] += 1;
  }
  return summary;
}

/**
 * Build a quote lifecycle timeline from existing timestamps (no new events API).
 * @param {Object} quote
 * @returns {{ at: Date, label: string, key: string }[]}
 */
export function buildQuoteTimeline(quote) {
  if (!quote) return [];
  const events = [];

  const push = (key, label, raw) => {
    if (!raw) return;
    const at = new Date(raw);
    if (Number.isNaN(at.getTime())) return;
    events.push({ key, label, at });
  };

  push("created", "Quote Created", quote.createdAt);
  push(
    "pricing",
    "Pricing Calculated",
    quote?.automatedPreparation?.completedAt ||
      quote?.automatedPreparation?.pricedAt ||
      quote?.pricingSuggestion?.calculatedAt
  );
  push("pdf", "PDF Created", quote?.quotePdf?.generatedAt);
  push("sent", "Quote Sent", quote.sentAt);
  push(
    "whatsapp",
    "WhatsApp Sent",
    quote?.lastDelivery?.channels?.whatsapp?.at
  );
  push("email", "Email Sent", quote?.lastDelivery?.channels?.email?.at);
  push("viewed", "Customer Viewed", quote.viewedAt || quote.customerViewedAt);

  if (Array.isArray(quote.negotiationHistory)) {
    for (const entry of quote.negotiationHistory) {
      push(
        `nego-${entry?.at || entry?.createdAt || events.length}`,
        entry?.note || entry?.action || "Negotiation Update",
        entry?.at || entry?.createdAt || entry?.timestamp
      );
    }
  }

  push(
    "proof",
    "Deposit Proof Received",
    quote?.payment?.proofReceivedAt
  );
  push("deposit_paid", "Deposit Paid", quote?.payment?.depositPaidAt);
  push(
    "deposit_verified",
    "Deposit Verified",
    quote?.payment?.depositVerifiedAt
  );

  if (quote.status === "ordered" || quote.orderId || quote?.payment?.orderId) {
    push(
      "ordered",
      "Order Created",
      quote?.orderedAt || quote?.updatedAt
    );
  }

  events.sort((a, b) => a.at.getTime() - b.at.getTime());
  return events;
}

/**
 * Invoice-style customer line rows from quote.items (priced lines) or a transport fallback.
 * @param {Object} quote
 * @returns {{ description: string, amount: number, quantity?: number }[]}
 */
const INTERNAL_COMMERCIAL_LINE_DESCRIPTIONS = new Set([
  "Customer markup",
  "Shared-load minimum",
]);

const INTERNAL_COMMERCIAL_LINE_RULE_KEYS = new Set([
  "economics.customer_markup",
  "economics.shared_min_uplift",
]);

/**
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
function isInternalCommercialCustomerLine(item) {
  if (!item || typeof item !== "object") return false;
  const ruleKey = String(item.ruleKey || "");
  if (INTERNAL_COMMERCIAL_LINE_RULE_KEYS.has(ruleKey)) return true;
  const description = String(item.description || "").trim();
  return INTERNAL_COMMERCIAL_LINE_DESCRIPTIONS.has(description);
}

export function getCustomerLineItems(quote) {
  const priced = Array.isArray(quote?.items)
    ? quote.items.filter(
        (item) =>
          item &&
          !isInternalCommercialCustomerLine(item) &&
          (Number(item.total) > 0 ||
            Number(item.unitPrice) > 0 ||
            item.description)
      )
    : [];

  if (priced.length > 0) {
    return priced.map((item) => {
      const qty = Number(item.quantity) || 1;
      const unit = Number(item.unitPrice) || 0;
      const total =
        item.total != null && Number.isFinite(Number(item.total))
          ? Number(item.total)
          : unit * qty;
      return {
        description: String(item.description || "Line item").trim(),
        amount: total,
        quantity: qty,
      };
    });
  }

  const total = getQuoteTotal(quote);
  if (total > 0) {
    const from = quote?.from || "Origin";
    const to = quote?.to || "Destination";
    return [
      {
        description: `Transport (${from} → ${to})`,
        amount: total,
        quantity: 1,
      },
    ];
  }

  return [];
}

/**
 * @param {Object} quote
 * @returns {"share_load"|"exclusive_load"}
 */
export function getQuoteMoveType(quote) {
  if (!quote) return "exclusive_load";
  if (quote.loadType === "share_load") return "share_load";
  if (quote.quoteType === "SL") return "share_load";
  if (quote.delivery?.type === "share_load") return "share_load";
  if (/-SL$/i.test(String(quote.referenceNumber || ""))) return "share_load";
  const explanation = quote.automatedPreparation?.pricingExplanation || {};
  const product = String(
    explanation.pricingProduct ||
      quote.automatedPreparation?.pricingProduct ||
      ""
  ).toLowerCase();
  if (
    product === "interprovince_shared" ||
    isSharedLoadType(explanation.serviceType) ||
    isSharedLoadType(quote.automatedPreparation?.service) ||
    isSharedLoadType(quote.serviceType)
  ) {
    return "share_load";
  }
  return "exclusive_load";
}

/**
 * @param {Object} quote
 * @returns {string}
 */
export function getQuoteMoveTypeLabel(quote) {
  return getQuoteMoveType(quote) === "share_load"
    ? formatServiceTypeLabel(SERVICE_TYPE.SHARED_LOAD)
    : formatServiceTypeLabel(SERVICE_TYPE.EXCLUSIVE_LOAD);
}

/**
 * @param {Object} quote
 * @returns {number|null}
 */
export function getQuoteDistanceKm(quote) {
  const candidates = [
    quote?.distanceKm,
    quote?.distance?.km,
    quote?.route?.distanceKm,
    quote?.automatedPreparation?.distanceKm,
    quote?.automatedPreparation?.distance?.km,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * @param {Object} quote
 * @returns {string}
 */
export function getQuoteDistanceLabel(quote) {
  const km = getQuoteDistanceKm(quote);
  if (km == null) return "—";
  return `${Math.round(km).toLocaleString("en-ZA")} km`;
}

/**
 * Default customer "what's included" bullets when quote has no override.
 * @param {Object} quote
 * @returns {string[]}
 */
export function getQuoteWhatsIncluded(quote) {
  const raw =
    quote?.sop2Messaging?.whatsIncluded ||
    quote?.whatsIncluded ||
    quote?.includedItems;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map(String).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(/\n|;/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  const shared = getQuoteMoveType(quote) === "share_load";
  return [
    shared
      ? "Shared truck (optimised routing)"
      : "Dedicated truck for your move only",
    "Door-to-door collection & delivery",
    "Professional loading & offloading",
    "Transit insurance during the move",
    "Full protective wrapping for furniture",
  ];
}

/**
 * Humanize pricing engine / product codes for display.
 * @param {unknown} engine
 * @returns {string}
 */
export function formatPricingEngineLabel(engine) {
  if (engine == null || engine === "" || engine === "—") return "—";
  if (typeof engine === "object") {
    const product =
      engine.pricingProduct || engine.product || engine.id || engine.name;
    return formatPricingEngineLabel(product);
  }
  const key = String(engine);
  const map = {
    SL: "Shared Load",
    ST: "Standard",
    PR: "Premium",
    shared_load: "Shared Load",
    interprovince_shared: "Interprovince Shared (corridor)",
    interprovince_exclusive: "Interprovince Exclusive",
    local_exclusive: "Local Exclusive",
    intra_province_exclusive: "Intra Province Exclusive",
    INTERPROVINCE_SHARED: "Interprovince Shared (corridor)",
    INTERPROVINCE_EXCLUSIVE: "Interprovince Exclusive",
    LOCAL_EXCLUSIVE: "Local Exclusive",
    INTRA_PROVINCE_EXCLUSIVE: "Intra Province Exclusive",
    SHARED_LOAD: "Shared Load",
  };
  if (map[key]) return map[key];
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Corridor may be a string ("WC - GP") or a catalogue object.
 * @param {unknown} corridor
 * @returns {string}
 */
export function formatCorridorLabel(corridor) {
  if (corridor == null || corridor === "") return "—";
  if (typeof corridor === "string" || typeof corridor === "number") {
    return String(corridor);
  }
  if (typeof corridor === "object") {
    const origin = corridor.originRegion || corridor.origin || corridor.from;
    const destination =
      corridor.destinationRegion || corridor.destination || corridor.to;
    if (origin && destination) return `${origin} - ${destination}`;
    if (corridor.id) return String(corridor.id);
    if (corridor.name) return String(corridor.name);
  }
  return "—";
}

/**
 * Internal-only snapshot for ops (never customer-facing).
 * @param {Object} quote
 * @returns {Record<string, string|number|null>}
 */
export function getQuoteInternalSnapshot(quote) {
  const prep = quote?.automatedPreparation || {};
  const volumes = prep.volumes || prep.volume || {};
  const explanation = prep.explanation || prep.pricingExplanation || {};
  const profitability =
    explanation.profitability && typeof explanation.profitability === "object"
      ? explanation.profitability
      : null;

  const engine =
    explanation.pricingProduct ||
    prep.pricingProduct ||
    prep.engine ||
    quote?.pricingProduct ||
    quote?.quoteType ||
    null;

  const billableVolumeM3 =
    explanation.billableVolumeM3 ??
    volumes.billableVolumeM3 ??
    prep.billableVolumeM3 ??
    quote?.billableVolumeM3 ??
    null;

  const ratePerM3 =
    explanation.ratePerM3 ??
    prep.ratePerM3 ??
    quote?.ratePerM3 ??
    null;

  const transportAmount =
    explanation.transportAmount ??
    explanation.finalTotal ??
    prep.transportAmount ??
    getQuoteTotal(quote);

  const expectedProviderCostRaw =
    profitability?.estimatedProviderCost ??
    prep.expectedProviderCost ??
    prep.providerCost ??
    explanation.expectedProviderCost ??
    null;
  const expectedProviderCost = Number.isFinite(Number(expectedProviderCostRaw))
    ? Number(expectedProviderCostRaw)
    : null;

  const customerTotal = getQuoteTotal(quote);
  const deposit = getQuoteDeposit(quote);
  const depositPercent =
    customerTotal > 0 && deposit > 0
      ? Math.round((deposit / customerTotal) * 100)
      : null;

  const revenueFromProfitability = Number(profitability?.grossMargin);
  const revenue =
    Number.isFinite(revenueFromProfitability)
      ? revenueFromProfitability
      : expectedProviderCost != null && Number.isFinite(customerTotal)
        ? customerTotal - expectedProviderCost
        : null;

  const marginFromProfitability = Number(profitability?.marginPercent);
  const marginPercent = Number.isFinite(marginFromProfitability)
    ? marginFromProfitability
    : revenue != null && customerTotal > 0
      ? Math.round((revenue / customerTotal) * 1000) / 10
      : null;

  const corridorRaw =
    explanation.corridor || prep.corridor || quote?.corridor || null;

  const rateFromCorridor =
    corridorRaw &&
    typeof corridorRaw === "object" &&
    Number.isFinite(Number(corridorRaw.ratePerM3))
      ? Number(corridorRaw.ratePerM3)
      : null;

  const minimumAmountRaw =
    explanation.minimumAmount ??
    (corridorRaw && typeof corridorRaw === "object"
      ? corridorRaw.minimumAmount
      : null);
  const minimumAmount = Number.isFinite(Number(minimumAmountRaw))
    ? Number(minimumAmountRaw)
    : null;

  const truckSize =
    explanation.selectedTruckSize ||
    explanation.recommendedTruckSize ||
    quote?.truckSize ||
    prep.truckSize ||
    null;

  return {
    engine: typeof engine === "string" ? engine : "—",
    engineLabel: formatPricingEngineLabel(engine),
    billableVolumeM3,
    ratePerM3: ratePerM3 ?? rateFromCorridor,
    minimumAmount,
    transportAmount,
    roundingAdjustment:
      explanation.roundingAdjustment ?? prep.roundingAdjustment ?? null,
    truckSize,
    loadType: quote?.loadType || quote?.delivery?.type || null,
    moveTypeLabel: getQuoteMoveTypeLabel(quote),
    corridor: formatCorridorLabel(corridorRaw),
    distanceKm: getQuoteDistanceKm(quote),
    expectedProviderCost,
    customerTotal,
    deposit,
    depositPercent,
    revenue,
    marginPercent,
  };
}
